import React, { useState, useEffect, useContext } from "react"
import i18n from "i18n"
import Dialog from "@mui/material/Dialog"
import CircularProgress from "@mui/material/CircularProgress"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import AuthContext from "context/auth/authContext"
import {
  isValidIban,
  normalizeIban,
} from "components/Invoices/utils/invoiceUtils"

const EMPTY_FIELDS = {
  name: "",
  address: "",
  phone: "",
  cif: "",
  iban: "",
  website: "",
}

const ModalGeneralInfoSettings = ({ open, onClose, organizationFields }) => {
  const [loading, setLoading] = useState(false)
  const [fields, setFields] = useState(EMPTY_FIELDS)

  const { user, updateUser, updateOrganization, getOrganizationNew } =
    useContext(AuthContext)

  useEffect(() => {
    if (!organizationFields || loading) return
    setFields({
      name: organizationFields.name || "",
      address: organizationFields.address || "",
      phone: organizationFields.phone || "",
      cif: organizationFields.cif_number || "",
      iban: organizationFields.iban || "",
      website: organizationFields.website || "",
    })
  }, [organizationFields, loading])

  const handleChange = field => event =>
    setFields(prev => ({ ...prev, [field]: event.target.value }))

  // Normalised on blur rather than on each keystroke, so typing an IBAN in readable
  // groups is not fought by the cursor jumping.
  const handleIbanBlur = () =>
    setFields(prev => ({ ...prev, iban: normalizeIban(prev.iban) }))

  const ibanIsInvalid = !!fields.iban && !isValidIban(fields.iban)

  const onSave = async () => {
    // Not relying on the blur: clicking Save does not always fire it first (Enter
    // submits, or the field never loses focus), and setFields is async anyway.
    const iban = normalizeIban(fields.iban)
    if (iban && !isValidIban(iban)) {
      setFields(prev => ({ ...prev, iban }))
      return
    }

    setLoading(true)
    try {
      await updateUser({
        "custom:nombre_empresa": fields.name,
        "custom:mobile": fields.phone,
        "custom:nif_empresa": fields.cif,
        "custom:web_page": fields.website,
        "custom:direccion_empresa": fields.address,
      })

      const orgId =
        organizationFields?.organizationId || user?.["custom:organizationId"]
      if (orgId) {
        await updateOrganization(orgId, {
          name: fields.name,
          phone: fields.phone,
          cif_number: fields.cif,
          iban,
          website: fields.website,
          address: fields.address,
        })
        try {
          await getOrganizationNew(orgId)
        } catch (e) {
          console.warn("Could not refresh organization after update", e)
        }
      }
    } finally {
      setLoading(false)
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("generalInfoSettings.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("generalInfoSettings.razonSocial")}
                size="small"
                variant="outlined"
                value={fields.name}
                onChange={handleChange("name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("generalInfoSettings.direccionFiscal")}
                size="small"
                variant="outlined"
                value={fields.address}
                onChange={handleChange("address")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("generalInfoSettings.cif")}
                size="small"
                variant="outlined"
                value={fields.cif}
                onChange={handleChange("cif")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("generalInfoSettings.iban")}
                size="small"
                variant="outlined"
                value={fields.iban}
                onChange={handleChange("iban")}
                onBlur={handleIbanBlur}
                error={ibanIsInvalid}
                helperText={
                  ibanIsInvalid ? i18n.t("generalInfoSettings.ibanInvalid") : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("generalInfoSettings.phone")}
                size="small"
                variant="outlined"
                value={fields.phone}
                onChange={handleChange("phone")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={i18n.t("generalInfoSettings.website")}
                size="small"
                variant="outlined"
                value={fields.website}
                onChange={handleChange("website")}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="branded"
          variant="outlined"
          disabled={loading}
        >
          {i18n.t("generalInfoSettings.cancel")}
        </Button>
        <Button
          onClick={onSave}
          color="branded"
          variant="contained"
          disabled={loading || ibanIsInvalid}
          sx={{ width: "106px", height: "36px" }}
        >
          {loading ? (
            <CircularProgress size={24} style={{ color: "white" }} />
          ) : (
            i18n.t("generalInfoSettings.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ModalGeneralInfoSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationFields: PropTypes.object,
}

export default ModalGeneralInfoSettings
